import React from 'react';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import locale from '@glu/locale';
import { withStyles } from '@glu/theming';
import { Button, SECONDARY, SMALL } from '@glu/buttons-react';
import { FilterAccordionWrapper } from '@glu/data-components';
import { PanelWrapper } from '@glu/grid-mobile-react';
import { updateGridView } from 'logic/store/grids/actions';
import MobileGridContext from '../logic/MobileGridContext';
import * as actions from '../logic/actions';
import getFilteredRows from '../logic/getFilteredRows';
import filterFunction from '../logic/filterFunction';

const FilterPanel = ({ classes, formattedColumnDefs }) => {
  const getFilterComponent = React.useCallback(filterFunction, []);
  const gridContext = React.useContext(MobileGridContext);
  const reduxDispatch = useDispatch();
  const columns = gridContext.columnsWithoutActions;
  return (
    <PanelWrapper
      title={locale.get('filters')}
      footer={
        <>
          <Button
            text={locale.get('apply')}
            size={SMALL}
            className={classes.mobileButtons}
            onClick={() => {
              const { paginatedData, totalRows } = getFilteredRows({
                columnState: gridContext.columnState,
                sortState: gridContext.sortState,
                filterState: gridContext.unAppliedFilterState,
                formattedColumnDefs,
                page: gridContext.page,
                pageSize: gridContext.pageSize,
                rows: gridContext.totalData
              });
              // Apply Button Click
              actions.saveFilters({
                gridContext,
                payload: {
                  filterState: gridContext.unAppliedFilterState,
                  data: paginatedData,
                  totalRows
                }
              });
              // Save grid state between page navigation
              updateGridView({
                gridId: gridContext.gridId,
                filterState: gridContext.unAppliedFilterState,
                dispatch: reduxDispatch
              });
            }}
          />
          <Button
            type="button"
            variant={SECONDARY}
            size={SMALL}
            className={classes.mobileButtons}
            onClick={() => {
              actions.resetFilters({ gridContext });
              gridContext.closeDrawer();
            }}
            text={locale.get('cancel')}
          />
        </>
      }
    >
      {columns.map((column) => {
        const { field, headerName, hide } = column;
        const unAppliedFilterState = gridContext.unAppliedFilterState || [];
        const filterValues = unAppliedFilterState.filter((filter) => filter.field === field);
        if (column.filterable === false) return null;
        return (
          <FilterAccordionWrapper
            key={field}
            htmlId={`filterField-${field}`}
            fieldId={field}
            label={headerName}
            hiddenField={hide}
            // If user has filter settings that will get applied then show clear button
            hasFilter={filterValues.length > 0}
            onClearFilter={(name) => actions.clearFilter({ gridContext, payload: { name } })}
            FilterComponent={getFilterComponent}
            value={filterValues}
            dark={false}
            column={column}
            name={field}
            // TODO: allow additional options from columns for use in filter component
            options={column.options}
            enumData={column.enumData}
            // TODO: margin/padding on left removed so that items align
          />
        );
      })}
    </PanelWrapper>
  );
};

FilterPanel.propTypes = {
  classes: PropTypes.objectOf(PropTypes.string).isRequired,
  formattedColumnDefs: PropTypes.arrayOf(PropTypes.shape({})).isRequired
};

export default withStyles({
  root: {
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    overflow: 'hidden',

    '@media (max-width: 767px)': {
      justifyContent: 'center'
    },

    '& form': {
      display: 'flex',
      flexDirection: 'column',
      height: '100%'
    }
  },

  actions: {
    display: 'flex',
    justifyContent: 'space-between',
    width: '100%',
    height: 50
  },

  mobileButtons: {
    borderRadius: 0,
    flexBasis: '50%',
    paddingLeft: 0
  },

  content: {
    margin: '10px 0 10px 10px',
    height: 'calc(100% - 70px)',
    overflow: 'auto'
  },

  contentWrapper: {
    padding: 0
  }
})(FilterPanel);
